/**
 * Important comments are located at the bottom of this script.
 *
 */
import { Suspense, lazy } from 'react';
import { PartialRouteObject } from 'react-router';
import { Navigate } from 'react-router-dom';

import BaseLayout from 'src/layouts/BaseLayout';
import SidebarLayout from 'src/layouts/SidebarLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import ViewExistingScormsForClient from './content/applications/Clients/ViewExistingScormsForClient';
import AddTeacherForm from './content/applications/Clients/AddTeacherForm';
import AllClientAgreementsTable from './content/applications/Clients/AllClientAgreementsTable';

import ProtectedRoute from './auth/ProtectedRoute';

/**
 * Loader function to wrap a component with Suspense for lazy loading.
 * @param {React.ComponentType} Component - The component to be lazy loaded.
 * @returns {React.FC} A function component wrapped with Suspense.
 */
const Loader = (Component) => (props) =>
(
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

// Pages

const Overview = Loader(lazy(() => import('src/content/overview')));

// Dashboards

const Crypto = Loader(lazy(() => import('src/content/dashboards/Crypto')));

// Applications

const Messenger = Loader(
  lazy(() => import('src/content/applications/Messenger'))
);
const Clients = Loader(lazy(() => import('src/content/applications/Clients')));

const ClientAgreements = Loader(
  lazy(() => import('src/content/applications/Clients/ClientAgreements'))
);

const ClientAgreementWorks = Loader(
  lazy(() => import('src/content/applications/Clients/ClientAgreementWorks'))
);

const AddAgreement = Loader(
  lazy(() => import('src/content/applications/Clients/AddAgreement'))
);

const DupeAgreement = Loader(
  lazy(() => import('src/content/applications/Clients/DupeAgreement'))
);

const AddAgreementWork = Loader(
  lazy(() => import('src/content/applications/Clients/AddAgreementWork'))
);

const AddNewClient = Loader(
  lazy(() => import('src/content/applications/Clients/AddNewClient'))
);
const UpdateClient = Loader(
  lazy(() => import('src/content/applications/Clients/UpdateClient'))
);
const GetClientAgreements = Loader(
  lazy(() => import('src/content/applications/Clients/GetClientAgreements'))
);

const UpdateClientAgreementCustomSkus = Loader(
  lazy(
    () =>
      import('src/content/applications/Clients/UpdateClientAgreementCustomSkus')
  )
);

const UpdateClientAgreementSCORM = Loader(
  lazy(
    () => import('src/content/applications/Clients/UpdateClientAgreementSCORM')
  )
);

const DownloadAllSCORM = Loader(
  lazy(() => import('src/content/applications/Clients/DownloadAllSCORM'))
);
const CreateSCORMFromAgreement = Loader(
  lazy(
    () => import('src/content/applications/Clients/CreateSCORMFromAgreement')
  )
);

const TeacherErrorHandler = Loader(
  lazy(
    () =>
      import('src/content/applications/TeacherAccessScorm/TeacherErrorHandler')
  )
);

const CreateOrder = Loader(
  lazy(() => import('src/content/applications/Transactions/DisabledPage'))
);
// Replace 'src/content/applications/Transactions/CreateOrder'

// Replace 'src/content/applications/Transactions/BulkCreateOrder'

const ExistingScormAccess = Loader(
  lazy(
    () =>
      import(
        'src/content/applications/TeacherAccessScorm/TeacherExistingScormAccess'
      )
  )
);

const TeacherScormDownload = Loader(
  lazy(
    () =>
      import('src/content/applications/TeacherAccessScorm/TeacherScormDownload')
  )
);

const TeacherDownloadAllScorm = Loader(
  lazy(
    () =>
      import(
        'src/content/applications/TeacherAccessScorm/TeacherDownloadAllScorm'
      )
  )
);

// Replace 'src/content/applications/ClientUsers/AddUser'

const ClientDetails = Loader(
  lazy(() => import('src/content/applications/Clients/ClientDetails'))
);

const AddProducts = Loader(
  lazy(() => import('src/content/applications/Clients/AddProducts'))
);
const Sets = Loader(
  lazy(() => import('src/content/applications/Clients/Sets'))
);
const UpdateSetsPage = Loader(
  lazy(() => import('src/content/applications/Clients/UpdateSetsPage'))
);
const ClientOrders = Loader(
  lazy(() => import('src/content/applications/Clients/ClientOrders'))
);

const UserProfile = Loader(
  lazy(() => import('src/content/applications/Users/profile'))
);
const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);

// Components

const Buttons = Loader(
  lazy(() => import('src/content/pages/Components/Buttons'))
);
const Modals = Loader(
  lazy(() => import('src/content/pages/Components/Modals'))
);
const Accordions = Loader(
  lazy(() => import('src/content/pages/Components/Accordions'))
);
const Tabs = Loader(lazy(() => import('src/content/pages/Components/Tabs')));
const Badges = Loader(
  lazy(() => import('src/content/pages/Components/Badges'))
);
const Tooltips = Loader(
  lazy(() => import('src/content/pages/Components/Tooltips'))
);
const Avatars = Loader(
  lazy(() => import('src/content/pages/Components/Avatars'))
);
const Cards = Loader(lazy(() => import('src/content/pages/Components/Cards')));
const Forms = Loader(lazy(() => import('src/content/pages/Components/Forms')));

// Status

const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status403 = Loader(
  lazy(() => import('src/content/pages/Status/Status403'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);
const ForogtPasssword = Loader(
  lazy(() => import('src/content/overview/Hero/ForogtPasssword'))
);

//Lazy load for retool and other apps
const AdminAppLoader = Loader(
  lazy(() => import('src/retool_apps/admin_app_loader'))
);
const ProductionAppLoader = Loader(
  lazy(() => import('src/retool_apps/production_app_loader'))
);
const ScormifyLogsAppLoader = Loader(
  lazy(() => import('src/retool_apps/scormify_logs_app_loader'))
);
const DocumentationLoader = Loader(
  lazy(() => import('src/retool_apps/documentation_loader'))
);
const AddApplicationUser = Loader(
  lazy(
    () =>
      import('src/content/applications/AddApplicationUsers/AddApplicationUsers')
  )
);

const QuoteGenerator = Loader(
  lazy(() => import('src/content/applications/Transactions/GenerateQuote'))
);

const adminRoutes: PartialRouteObject[] = [
  {
    path: '/',
    element: (
      <ProtectedRoute role={['billing_admin']}>
        <Navigate to="/dashboards/crypto" replace />
      </ProtectedRoute>
    )
  },
  {
    path: '*',
    element: (
      <ProtectedRoute role={['billing_admin']}>
        <BaseLayout />
      </ProtectedRoute>
    ),
    children: [
      // {
      //   path: '/',
      //   element: <Overview />
      // },
      {
        path: '/',
        element: (
          <ProtectedRoute role={['billing_admin']}>
            <Navigate to="/dashboards/crypto" replace />
          </ProtectedRoute>
        )
      },
      {
        path: 'status',
        children: [
          {
            path: '/',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: '/403',
    element: <Status403 />
  },
  {
    path: 'dashboards',
    element: (
      <ProtectedRoute role={['billing_admin']}>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'admin_app',
        element: <AdminAppLoader />
      },
      {
        path: 'production_app',
        element: <ProductionAppLoader />
      },
      {
        path: 'scormify_logs_app_loader',
        element: <ScormifyLogsAppLoader />
      },
      {
        path: 'documentation_app',
        element: <DocumentationLoader />
      },
      {
        path: '/',
        element: <Navigate to="/dashboards/crypto" replace />
      },
      {
        path: 'crypto',
        element: <Crypto />
      },
      {
        path: 'messenger',
        element: <Messenger />
      },
      {
        path: 'add-new',
        element: <AddNewClient />
      },
      {
        path: 'update-client',
        element: <UpdateClient />
      },
      {
        path: 'agreement-work-edit-client',
        element: <UpdateClientAgreementCustomSkus />
      },
      {
        path: 'existing-scorm-access',
        element: <ViewExistingScormsForClient />
      },
      {
        path: 'agreement-scorm-edit-client',
        element: <UpdateClientAgreementSCORM />
      },
      {
        path: 'get-client-agreements',
        element: <GetClientAgreements />
      },
      {
        path: 'download-all-scorm-client',
        element: <DownloadAllSCORM />
      },
      {
        path: 'agreement-work-scorm-client',
        element: <CreateSCORMFromAgreement />
      },
      {
        path: 'client-details',
        element: <ClientDetails />
      },
      {
        path: 'client-agreements',
        element: <ClientAgreements />
      },
      {
        path: 'client-agreements-works',
        element: <ClientAgreementWorks />
      },
      {
        path: 'add-agreement',
        element: <AddAgreement />
      },
      {
        path: 'duplicate-agreement',
        element: <DupeAgreement />
      },
      {
        path: 'add-agreement-work',
        element: <AddAgreementWork />
      },
      {
        path: 'add-products',
        element: <AddProducts />
      },
      {
        path: 'add-sets',
        element: <Sets />
      },
      {
        path: 'update-sets',
        element: <UpdateSetsPage />
      },
      {
        path: 'view-orders',
        element: <ClientOrders />
      },
      {
        path: 'create-order',
        element: <CreateOrder />
      }
    ]
  },
  {
    path: 'management',
    element: (
      <ProtectedRoute role={['billing_admin']}>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/management/clients" replace />
      },
      {
        path: 'clients',
        element: <Clients />
      },
      {
        path: 'profile',
        children: [
          {
            path: '/',
            element: <Navigate to="details" replace />
          },
          {
            path: 'details',
            element: <UserProfile />
          },
          {
            path: 'settings',
            element: <UserSettings />
          }
        ]
      },
      {
        path: 'addApplicationUser',
        element: <AddApplicationUser />
      },
      {
        path: 'newQuote',
        element: <QuoteGenerator />
      },

      {
        path: 'new-teacher-form',
        element: <AddTeacherForm />
      },
      {
        path: 'all-agreements-table',
        element: <AllClientAgreementsTable />
      }
    ]
  },
  {
    path: 'components',
    element: (
      <ProtectedRoute role={['billing_admin']}>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/components/buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  }
];

const withErrorBoundary = (element: JSX.Element) => (
  <TeacherErrorHandler>
    <Suspense
      fallback={
        <div
          aria-busy="true"
          aria-live="polite"
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            background: 'transparent'
          }}
        >
          <div className="spinner" />
        </div>
      }
    >
      {element}
    </Suspense>
  </TeacherErrorHandler>
);

const clientRoutes: PartialRouteObject[] = [
  {
    path: '/',
    element: withErrorBoundary(
      <ProtectedRoute role={['billing_client']}>
        <Navigate to="/management/recent-scorm" replace />
      </ProtectedRoute>
    )
  },
  {
    path: '*',
    element: withErrorBoundary(
      <ProtectedRoute role={['billing_client']}>
        <Navigate to="/management/recent-scorm" replace />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/management/recent-scorm" replace />
      },
      {
        path: '/dashboards/crypto',
        element: <Navigate to="/management/recent-scorm" replace />
      },
      {
        path: 'status',
        children: [
          { path: '/', element: <Navigate to="404" replace /> },
          { path: '404', element: <Status404 /> },
          { path: '500', element: <Status500 /> },
          { path: 'maintenance', element: <StatusMaintenance /> },
          { path: 'coming-soon', element: <StatusComingSoon /> }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: '/403',
    element: <Status403 />
  },
  {
    path: 'dashboards',
    element: withErrorBoundary(
      <ProtectedRoute role={['billing_client']}>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '/', element: <Navigate to="recent-scorm" replace /> },
      {
        path: '/dashboards/crypto',
        element: <Navigate to="recent-scorm" replace />
      },
      /*
      { path: 'crypto', element: <Crypto /> },
      { path: 'create-order', element: <CreateOrder /> },
      { path: 'bulk-create-order', element: <BulkCreateOrder /> },
      { path: 'order-detail', element: <OrderDetails /> },
      { path: 'add-user', element: <AddUsers /> }
      */
      { path: 'recent-scorm', element: <ExistingScormAccess /> },
      {
        path: 'agreement-work-teacher-scorm-client',
        element: <TeacherScormDownload />
      },
      { path: 'download-scorm-teacher', element: <TeacherDownloadAllScorm /> },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },

  {
    path: 'management',
    element: withErrorBoundary(
      <ProtectedRoute role={['billing_client']}>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/management/recent-scorm" replace />
      },
      /*
      { path: 'transactions', element: <Transactions /> },
      { path: 'users', element: <ClientUser /> },
      { path: 'clients', element: <Clients /> },
      */
      { path: 'recent-scorm', element: <ExistingScormAccess /> },
      {
        path: 'agreement-work-teacher-scorm-client',
        element: <TeacherScormDownload />
      },
      { path: 'download-scorm-teacher', element: <TeacherDownloadAllScorm /> },
      {
        path: 'profile',
        children: [
          { path: '/', element: <Navigate to="details" replace /> },
          { path: 'details', element: <UserProfile /> },
          { path: 'settings', element: <UserSettings /> }
        ]
      },

      {
        path: '*',
        element: <Status404 />
      }
    ]
  },

  {
    path: 'components',
    element: withErrorBoundary(
      <ProtectedRoute role={['billing_client']}>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '/', element: <Navigate to="/components/buttons" replace /> },
      { path: 'buttons', element: <Buttons /> },
      { path: 'modals', element: <Modals /> },
      { path: 'accordions', element: <Accordions /> },
      { path: 'tabs', element: <Tabs /> },
      { path: 'badges', element: <Badges /> },
      { path: 'tooltips', element: <Tooltips /> },
      { path: 'avatars', element: <Avatars /> },
      { path: 'cards', element: <Cards /> },
      { path: 'forms', element: <Forms /> }
    ]
  }
];

export const LoginRoutes: PartialRouteObject[] = [
  {
    path: '/',
    element: <Overview />
  },
  {
    path: '/forgot',
    element: <ForogtPasssword />
  },
  {
    path: '*',
    element: <Navigate to="/" replace />
  }
];

/**
 * ## Explanation of `router.tsx`
 *
 * The `router.tsx` file is responsible for defining the routing structure for the React application. It uses the `react-router-dom` library to manage navigation and rendering of different components based on the URL path.
 *
 * ### Imports
 *
 * - **React & React Router**: It imports necessary components like `Suspense`, `lazy`, and `Navigate` from React and `react-router-dom`.
 * - **Layouts**: `SidebarLayout` and `BaseLayout` are imported, which define the general structure of the pages.
 * - **Loaders**: Loaders like `AdminAppLoader`, `ProductionAppLoader`, and `DocumentationLoader` are imported, for loading specific parts of the application.
 * - **Suspense Loader**: `SuspenseLoader` imported to display a loading indicator while components are being lazily loaded.
 * - **Local User**: The `localUser` fetched from local storage to determine the role of the user and adjust routes.
 *
 * ### Loader Function
 *
 * - The `Loader` function wraps a component with `Suspense` and `SuspenseLoader` to handle lazy loading, ensuring that a loading spinner is shown while the component is being loaded.
 *
 * ### Lazy-loaded Components
 *
 * - Pages and components are lazy-loaded using `React.lazy` and wrapped with the `Loader` function. This improves the application's performance by splitting the code and loading components only when needed.
 *
 * ### Routes Configuration
 *
 * - **Admin Routes**: Defined in the `adminRoutes` array, these routes are only accessible to users with the `admin` role. Include paths for various dashboards, applications, and management features.
 * - **Client Routes**: Defined in the `clientRoutes` array, these routes are for regular users. Include fewer paths, focusing on dashboards and some management features.
 * - **Login Routes**: Defined in the `LoginRoutes` array, these routes handle login and error pages.
 *
 * ### Conditional Export
 *
 * - The routes to be exported and used by the application depend on the user's role (`admin` or regular user). This is determined by checking `localUser?.role`.
 *
 * ## Adding a New Page
 *
 * To add a new page to your project, follow these steps:
 *
 * 1. **Create the Component**:
 * - Create your new page component in the appropriate directory. For example, if you're adding a new dashboard, you might place it in `src/content/dashboards/`. Create a new file, e.g., `MyNewPage.tsx`, and define your component there.
 *
 * ```tsx
 *  import React from 'react';
 *    const MyNewPage: React.FC = () => {
 *      return (
 *        <div>
 *          <h1>My New Page</h1>
 *          <p>This is the content of my new page.</p>
 *        </div>
 *      );
 *    };
 *  export default MyNewPage;
 * ```
 *
 * 2. **Import and Lazy Load the Component**:
 *
 * - Import and wrap your new component with the Loader function in router.tsx.
 *
 * ```tsx
 *  const MyNewPage = Loader(lazy(() => import('src/content/dashboards/MyNewPage')));
 * ```
 *
 * 3. **Add a Route for the New Page**:
 *
 * - Add a new route object to the appropriate routes array (adminRoutes, clientRoutes, or LoginRoutes). For example, if it's an admin page:
 *
 * ```tsx
 *  const adminRoutes: PartialRouteObject[] = [
 *    // ...existing routes
 *    {
 *      path: 'dashboards',
 *      element: <SidebarLayout />,
 *      children: [
 *        // ...existing routes
 *        {
 *          path: 'my-new-page',
 *          element: <MyNewPage />
 *        },
 *      ]
 *    },
 *  // ...other routes
 *  ];
 * ```
 *  - Go to `/dashboards/my-new-page` to access your new page.
 *
 * 4. **Update Navigation to new page**:
 *
 * - If your application has a sidebar or navigation menu, ensure to update it to include a link to your new page. This might be in a separate component or configuration file where navigation items are defined.
 * - To link to this new page you can use the code snippets below and through useNavigate() you can send information (a dict) to the page.
 *
 * ```tsx
 *  import { Link } from 'react-router-dom';
 *  const navigate = useNavigate();
 *  <div>
 *    <Button component={Link} to="/dashboards/my-new-page" state={{ message: 'Hello from the previous page!' }}>
 *      Go to My New Page
 *    </Button>
 *  </div>
 *
 *  or
 *
 *  const navigate = useNavigate();
 *  const handleClick = () => {
 *    //Add some additional logic here.
 *    navigate('/dashboards/my-new-page', { state: { message: 'Hello from the previous page!' } });
 *  };
 *
 *  <div>
 *    <Button onClick={handleClick}>
 *      Go to My New Page
 *    </Button>
 *  </div>
 *
 * ```
 *
 * */

const RouteSplitter = (user_role?: string) => {
  if (user_role === 'billing_admin') {
    return [...adminRoutes, ...LoginRoutes];
  }

  if (user_role === 'billing_client') {
    return [...clientRoutes, ...LoginRoutes];
  }

  return [...LoginRoutes];
};

export default RouteSplitter;
